'use client';

import { useRouter } from 'next/navigation';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { User, onAuthStateChanged, onIdTokenChanged, signOut as signOutFn, GoogleAuthProvider, signInWithPopup, signInWithCustomToken as signInWithCustomTokenFn } from 'firebase/auth';

// Constants
import { COOKIE_KEYS_SHARED, DEFAULT_EMAIL_DOMAIN_LIST, MESSAGES, ROUTES } from '@shared/constants';
import { AUTH_COOKIE_CONFIG, COOKIE_KEYS, FORCE_REFRESH_TOKEN_INTERVAL } from '@/constants';

// Services
import { auth } from '@/services/firebase';

// Configs
import ENV from '@shared/configs';

// Utils
import { cookieStorage } from '@shared/utils';
import { showErrorToast } from '@/utils/errorHandlers';
import { getFullUrl } from '@/utils';

// Actions
import { getCustomToken } from '@/actions/client/auth';

// Stores
import { appStoreActions } from '@/stores';
interface FirebaseType {
  user: User | null;
  isLoading: boolean;
  signIn: (redirectTo?: string, hasMiddlewareRedirect?: boolean) => Promise<void>;
  signOut: () => Promise<void>;
  getToken: () => Promise<string | undefined>;
}
interface FirebaseProviderProps {
  children: ReactNode;
}
const Firebase = createContext<FirebaseType | undefined>(undefined);
export const useFirebase = () => {
  const context = useContext(Firebase);
  if (context === undefined) {
    throw new Error('useFirebase must be used within a FirebaseProvider');
  }
  return context;
};
export const FirebaseProvider = ({
  children
}: FirebaseProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const router = useRouter();
  const isSigningIn = useRef(false);
  const {
    domain
  } = AUTH_COOKIE_CONFIG;
  const currentOrigin = getFullUrl()?.origin;
  const authConfig = useMemo(() => ({
    ...AUTH_COOKIE_CONFIG,
    domain: currentOrigin?.includes(domain) ? domain : ''
  }), [currentOrigin, domain]);
  const clearTokenCookies = useCallback(() => {
    cookieStorage.removeItem(COOKIE_KEYS.TOKEN);
    cookieStorage.removeItem(COOKIE_KEYS.TOKEN_SHARED, authConfig);
    cookieStorage.removeItem(COOKIE_KEYS_SHARED.EMAIL_USER_SHARED, authConfig);
  }, [authConfig]);

  /**
   * Attempts to authenticate the user using a custom token flow.
   *
   * This function checks for a stored refresh token in cookies and, if found,
   * exchanges it for a Firebase custom token via the getCustomToken API.
   * If successful, it signs the user in with Firebase auth and redirects to the
   * intended URL (if available) or home.
   * If no token is available, it signs the user out.
   */
  const signInWithCustomToken = useCallback(async () => {
    const token = cookieStorage.getItem(COOKIE_KEYS.TOKEN_SHARED);
    if (isInitialized && !user && token) {
      try {
        const {
          data,
          error
        } = await getCustomToken({
          requestConfig: {
            token
          }
        });
        if (error || !data) {
          showErrorToast(error);
          clearTokenCookies();
          cookieStorage.clearAllItems();
          router.push(ROUTES.SIGN_IN);
          setIsLoading(false);
          return;
        }
        const {
          token: customToken
        } = data || {};
        await signInWithCustomTokenFn(auth, customToken || '');

        // Check for intended URL (set by middleware) before redirecting
        const intendedUrl = cookieStorage.getItem(COOKIE_KEYS.INTENDED_URL);
        const redirectUrl = intendedUrl ? decodeURIComponent(intendedUrl) : ROUTES.HOME;

        // Clear the intended URL cookie after reading
        if (intendedUrl) {
          cookieStorage.removeItem(COOKIE_KEYS.INTENDED_URL);
        }

        // Direct signin, just replace
        router.replace(redirectUrl);
      } catch (error: unknown) {
        showErrorToast(error);
      }
    }
    if (!token && isInitialized) {
      await signOut();
    }
  }, [isInitialized, user, router, clearTokenCookies]);
  const revalidateUserSession = useCallback(async () => {
    setIsLoading(true);
    cookieStorage.clearAllItems();
    // Clear all stores
    appStoreActions.clearAllStores();
    await signOutFn(auth);
    await signInWithCustomToken();

    // Reload page for get all the data
    window.location.reload();
  }, [signInWithCustomToken]);
  const updateToken = useCallback(async (user: User | null, forceRefresh = false) => {
    if (user) {
      setUser(user);
      const emailUserShared = cookieStorage.getItem(COOKIE_KEYS_SHARED.EMAIL_USER_SHARED);
      if (user?.email && emailUserShared && user.email !== emailUserShared) {
        await revalidateUserSession();
        return;
      }
      const token = await user.getIdToken(forceRefresh);
      cookieStorage.setItem(COOKIE_KEYS.TOKEN, token);
      cookieStorage.setItem(COOKIE_KEYS.TOKEN_SHARED, user.refreshToken, authConfig);
      cookieStorage.setItem(COOKIE_KEYS_SHARED.EMAIL_USER_SHARED, user.email || '', authConfig);
    } else {
      clearTokenCookies();
    }
  }, [clearTokenCookies, revalidateUserSession, authConfig]);
  const forceRefreshToken = useCallback(async () => {
    const currentUser = auth.currentUser;
    if (!currentUser || isSigningIn.current) return;
    try {
      await updateToken(currentUser, true);
    } catch (error: unknown) {
      showErrorToast(error);
    }
  }, [updateToken]);
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async user => {
      setIsInitialized(true);
      setIsLoading(true);
      const token = cookieStorage.getItem(COOKIE_KEYS.TOKEN_SHARED);
      if (!user && token) return;
      updateToken(user);
      setIsLoading(false);
    });
    return () => unsubscribe();

    // Don't need to put "updateToken" to the dependencies array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    signInWithCustomToken();
  }, [signInWithCustomToken]);
  useEffect(() => {
    if (!user || !isInitialized) return;
    const refreshTokenInterval = window.setInterval(() => {
      void forceRefreshToken();
    }, FORCE_REFRESH_TOKEN_INTERVAL);
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void forceRefreshToken();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.clearInterval(refreshTokenInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [forceRefreshToken, isInitialized, user]);

  // Automatically redirect to sign-in page if the user is not authenticated
  useEffect(() => {
    const handleFocus = async () => {
      // Skip cleanup when a sign-in popup is in progress to prevent
      // clearing intended_url cookie before redirect can use it
      if (!isLoading && !isSigningIn.current) {
        const user = auth.currentUser;
        const tokenShared = cookieStorage.getItem(COOKIE_KEYS.TOKEN_SHARED);
        const emailUserShared = cookieStorage.getItem(COOKIE_KEYS_SHARED.EMAIL_USER_SHARED);
        if (user?.email && emailUserShared && user.email !== emailUserShared) {
          await revalidateUserSession();
          return;
        }
        if (!user && !tokenShared || user && !tokenShared) {
          clearTokenCookies();
          router.replace(ROUTES.SIGN_IN);
          cookieStorage.clearAllItems();
          await signOutFn(auth);
          // Clear all stores
          appStoreActions.clearAllStores();
        }
        if (!user && tokenShared) {
          // If the user is not authenticated but tokenShared exists, try to sign in
          signInWithCustomToken();
        }
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [isLoading, signInWithCustomToken, clearTokenCookies, router, revalidateUserSession]);
  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async user => {
      if (!isLoading && isInitialized) {
        updateToken(user);
      }
    });
    return () => unsubscribe();

    // Don't need to put "updateToken" to the dependencies array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isInitialized]);
  const signIn = async (redirectTo = ROUTES.HOME, hasMiddlewareRedirect = false) => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: 'select_account'
    });

    // Guard: prevent handleFocus from clearing cookies during popup flow
    isSigningIn.current = true;
    try {
      const {
        NEXT_PUBLIC_ALLOW_EMAIL_SIGN_IN
      } = ENV;
      const {
        user
      } = await signInWithPopup(auth, provider);
      const isValidEmail = DEFAULT_EMAIL_DOMAIN_LIST.some((domain: string) => user.email?.endsWith(domain)) || NEXT_PUBLIC_ALLOW_EMAIL_SIGN_IN.includes(user.email || '');
      if (!isValidEmail) {
        // Sign out the user if the email is not allowed
        await signOutFn(auth);
        showErrorToast(MESSAGES.EMAIL_NOT_ALLOWED(user.email || ''));
        return;
      }
      if (hasMiddlewareRedirect) {
        //Replace current (sign-in) with home
        window.history.replaceState(null, '', ROUTES.HOME);

        // Navigate to intended destination,This creates: [home] -> [redirectTo]
        window.location.href = redirectTo;
      } else {
        // Direct signin, just replace
        router.replace(redirectTo);
      }
    } finally {
      isSigningIn.current = false;
    }
  };
  const signOut = async () => {
    await signOutFn(auth);
  };
  const getToken = async () => {
    if (!user) return;
    const token = await user.getIdToken();
    return token;
  };
  const value: FirebaseType = {
    user,
    isLoading,
    signIn,
    signOut,
    getToken
  };
  return <Firebase.Provider value={value} data-sentry-element="unknown" data-sentry-component="FirebaseProvider" data-sentry-source-file="firebase.tsx">{children}</Firebase.Provider>;
};